import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'

export const ASSOCIATED_PARTY_TYPES = [
  'MANUFACTURER',
  'CONSULTANT',
  'AUTHORISED_REPRESENTATIVE',
] as const

export type AssociatedPartyType = (typeof ASSOCIATED_PARTY_TYPES)[number]

export type AssociatedParty = {
  id: string
  organizationId: string
  name: string
  street?: string | null
  street2?: string | null
  streetNumber?: string | null
  subDivision?: string | null
  country?: string | null
  postCode?: string | null
  poBox?: string | null
  city?: string | null
  contactName?: string | null
  contactPhoneNumber?: string | null
  contactEmail?: string | null
  createdAt: string
  updatedAt: string
  deletedAt?: string | null
}

export type AssociatedPartyProductAssignment = {
  machineryDirectiveComplianceId: string | null
  productId: string
  productName: string | null
  productModel: string | null
  role: AssociatedPartyType
  completed: boolean
}

export type AssociatedPartyDetails = AssociatedParty & {
  productAssignments: AssociatedPartyProductAssignment[]
}

export type ProductAssociatedPartyAssignment = {
  associatedParty: AssociatedParty
  role: AssociatedPartyType
}

export type CreateAssociatedPartyPayload = {
  name: string
  street?: string | null
  street2?: string | null
  streetNumber?: string | null
  subDivision?: string | null
  country: string
  postCode?: string | null
  poBox?: string | null
  city: string
  contactName?: string | null
  contactPhoneNumber?: string | null
  contactEmail?: string | null
}

export type UpdateAssociatedPartyPayload = CreateAssociatedPartyPayload

export const ASSOCIATED_PARTIES_KEY = 'associatedParties'
export const PRODUCT_ASSOCIATED_PARTIES_KEY = 'productAssociatedParties'

export function useListAssociatedParties(options?: { enabled?: boolean }) {
  return useQuery<AssociatedParty[], Error>({
    queryKey: [ASSOCIATED_PARTIES_KEY, 'list'],
    queryFn: async () => {
      const response = await apiClient.get<AssociatedParty[]>(
        '/v1/associated-parties'
      )
      return response.data
    },
    enabled: options?.enabled ?? true,
  })
}

export function useGetAssociatedParty(associatedPartyId: string | undefined) {
  return useQuery<AssociatedPartyDetails | null, Error>({
    queryKey: [ASSOCIATED_PARTIES_KEY, 'detail', associatedPartyId],
    queryFn: async () => {
      if (!associatedPartyId) {
        throw new Error('Associated party ID is required')
      }
      try {
        const response = await apiClient.get<AssociatedPartyDetails>(
          `/v1/associated-parties/${associatedPartyId}`
        )
        return response.data
      } catch (error: unknown) {
        const status =
          error && typeof error === 'object' && 'response' in error
            ? (error as { response?: { status?: number } }).response?.status
            : undefined
        if (status === 404) {
          return null
        }
        throw error
      }
    },
    enabled: !!associatedPartyId,
  })
}

export function useCreateAssociatedParty() {
  const queryClient = useQueryClient()
  return useMutation<AssociatedParty, Error, CreateAssociatedPartyPayload>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<AssociatedParty>(
        '/v1/associated-parties',
        payload
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ASSOCIATED_PARTIES_KEY] })
    },
  })
}

export function useUpdateAssociatedParty(associatedPartyId: string) {
  const queryClient = useQueryClient()

  return useMutation<AssociatedParty, Error, UpdateAssociatedPartyPayload>({
    mutationFn: async (payload) => {
      const response = await apiClient.patch<AssociatedParty>(
        `/v1/associated-parties/${associatedPartyId}`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ASSOCIATED_PARTIES_KEY] })
      void queryClient.invalidateQueries({
        queryKey: [ASSOCIATED_PARTIES_KEY, 'detail', associatedPartyId],
      })
    },
  })
}

export function useDeleteAssociatedParty(associatedPartyId: string) {
  const queryClient = useQueryClient()

  return useMutation<AssociatedParty, Error, void>({
    mutationFn: async () => {
      const response = await apiClient.delete<AssociatedParty>(
        `/v1/associated-parties/${associatedPartyId}`
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ASSOCIATED_PARTIES_KEY] })
      void queryClient.invalidateQueries({
        queryKey: [ASSOCIATED_PARTIES_KEY, 'detail', associatedPartyId],
      })
    },
  })
}

export function useListAssociatedPartiesForProduct(
  productId: string | undefined
) {
  return useQuery<ProductAssociatedPartyAssignment[], Error>({
    queryKey: [PRODUCT_ASSOCIATED_PARTIES_KEY, productId],
    queryFn: async () => {
      if (!productId) {
        throw new Error('Product ID is required')
      }
      const response = await apiClient.get<ProductAssociatedPartyAssignment[]>(
        `/products/${productId}/associated-parties`
      )
      return response.data
    },
    enabled: !!productId,
  })
}

export function useAssignAssociatedPartyToProduct(
  productId: string | undefined
) {
  const queryClient = useQueryClient()

  return useMutation<
    ProductAssociatedPartyAssignment,
    Error,
    { associatedPartyId: string; role: AssociatedPartyType }
  >({
    mutationFn: async ({ associatedPartyId, role }) => {
      if (!productId) {
        throw new Error('Product ID is required')
      }
      const response = await apiClient.post<ProductAssociatedPartyAssignment>(
        `/products/${productId}/associated-parties/${associatedPartyId}`,
        { role }
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCT_ASSOCIATED_PARTIES_KEY, productId],
      })
    },
  })
}

export function useUnassignAssociatedPartyFromProduct(
  productId: string | undefined
) {
  const queryClient = useQueryClient()

  return useMutation<
    void,
    Error,
    { associatedPartyId: string; role: AssociatedPartyType }
  >({
    mutationFn: async ({ associatedPartyId, role }) => {
      if (!productId) {
        throw new Error('Product ID is required')
      }
      await apiClient.delete(
        `/products/${productId}/associated-parties/${associatedPartyId}`,
        { params: { role } }
      )
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCT_ASSOCIATED_PARTIES_KEY, productId],
      })
    },
  })
}
